/**
 * CoverageCircle
 *
 * Compact SVG donut chart showing ingredient coverage percentage.
 * Color-coded: green >= 90%, amber >= 60%, red < 60%.
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Svg, { Circle } from 'react-native-svg';

const COVERAGE_COLORS = {
  green: '#2E7D32',
  greenBg: '#E8F5E9',
  amber: '#F9A825',
  amberBg: '#FFF8E1',
  red: '#E53935',
  redBg: '#FFEBEE',
} as const;

function getCoverageColor(percent: number): { stroke: string; bg: string } {
  if (percent >= 90) return { stroke: COVERAGE_COLORS.green, bg: COVERAGE_COLORS.greenBg };
  if (percent >= 60) return { stroke: COVERAGE_COLORS.amber, bg: COVERAGE_COLORS.amberBg };
  return { stroke: COVERAGE_COLORS.red, bg: COVERAGE_COLORS.redBg };
}

interface CoverageCircleProps {
  coveragePercent: number;
  size?: number;
}

export function CoverageCircle({ coveragePercent, size = 44 }: CoverageCircleProps) {
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - coveragePercent / 100);
  const { stroke, bg } = getCoverageColor(coveragePercent);

  return (
    <View style={[styles.container, { width: size, height: size }]}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={bg}
          strokeWidth={strokeWidth}
        />
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={`${circumference}`}
          strokeDashoffset={offset}
          strokeLinecap="round"
          rotation={-90}
          origin={`${size / 2}, ${size / 2}`}
        />
      </Svg>
      <View style={styles.labelContainer}>
        <Text style={[styles.label, { color: stroke, fontSize: size <= 36 ? 9 : 11 }]}>
          {coveragePercent}%
        </Text>
      </View>
    </View>
  );
}

export { COVERAGE_COLORS, getCoverageColor };

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  labelContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    fontWeight: '700',
  },
});
